import * as React from 'react';
import {useCallback, useState} from 'react';

import memoize from 'memoize-one';
import {labelComponent, useStepWithRollback} from '../util/media';
import {Loader} from 'semantic-ui-react';
import {useLoadFile, FileInfo} from '../util/requests';
import {runLink} from '../util/runhelpers';
import MessageMediaNotFound from './MessageMediaNotFound';
import {MediaCardProps} from './MediaCard';
import {RunHistoryRow} from '../types/run';
import makeComp from '../util/profiler';
import BokehViewer from './Panel2/BokehViewer';

// a single image card
const BokehCard = makeComp(
  (props: MediaCardProps) => {
    const {run, mediaKey, mediaIndex, runSignature, height, width} = props;

    const {currentStep, stepData, failedToLoad, rollback} = useStepWithRollback(
      props
    );

    let mediaPath = getMedia(stepData, mediaKey, mediaIndex);

    const titleLink = runLink(runSignature, run.displayName, {
      className: 'hide-in-run-page',
      target: '_blank',
      rel: 'noopener noreferrer',
    });

    const [bokehJson, setBokehJson] = useState<string>();

    const onSuccess = useCallback(async (file: File, fileInfo: FileInfo) => {
      setBokehJson(JSON.parse(await new Response(file).text()));
    }, []);

    // Use empty string when we have null and skip the request
    mediaPath = mediaPath || '';
    // ! Warning: The callbacks passed to useLoadFile must stay
    // equal by reference or it will retrigger a new file download.
    const loading = useLoadFile(runSignature, mediaPath, {
      responseType: 'file',
      onSuccess,
      fallback: rollback,
      skip: mediaPath === '' || failedToLoad,
    });

    if (loading || bokehJson == null) {
      return (
        <div style={{width, height}}>
          <Loader active />
        </div>
      );
    }

    return (
      <BokehViewer
        headerElements={labelComponent(props, currentStep, titleLink)}
        notFoundElements={[
          <MessageMediaNotFound
            key={mediaKey}
            mediaKey={mediaKey}
            stepIndex={currentStep}
            mediaIndex={mediaIndex}
            mediaType="bokeh"
          />,
        ]}
        contentNotFound={!mediaPath}
        bokehJson={bokehJson}
      />
    );
  },
  {id: 'BokehCard'}
);

export default BokehCard;

// Get the Media object logged for the given key and step.
const getMedia = memoize(
  (
    stepData: RunHistoryRow | undefined,
    mediaKey: string,
    index: number
  ): string | null => {
    if (!stepData || !stepData[mediaKey]) {
      return null;
    }
    const stepMedia = stepData[mediaKey];
    if (stepMedia._type === 'bokeh') {
      if (stepMedia.filenames && stepMedia.filenames[index]) {
        return `media/bokeh/${stepMedia.filenames[index]}`;
      } else {
        return null;
      }
    } else if (stepMedia._type === 'bokeh-file') {
      return stepMedia.path;
    } else {
      return null;
    }
  }
);
